import { AlertTriangle, Bot, Check, Copy, CreditCard, ExternalLink, Globe, Package, Sparkles, Wand2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useBillSummary from "../../hooks/useBillSummary";
import useEntity from "../../hooks/useEntity";
import useStats from "../../hooks/useStats";
import useToastStore from "../../store/useToastStore";
import OrderList from "../order/OrderList";
import DailyStatsChart from "./DailyStatsChart";
import { DashboardFilter } from "./DashboardFilter";
import { StatsCard } from "./StatsCard";
import { WhatsAppSupportButton } from "./WhatsAppSupportButton";
import { DashboardRange, DEFAULT_RANGE, rangeLabel } from "../../types/dashboardRange";
import { card, chip, pageBg, sectionSub, sectionTitle } from "./dashboardTheme";

// Module-scoped so it survives route navigation (component remounts) but resets
// on a full page refresh / reopen — shows the overdue popup once per page load.
let overdueDialogShown = false;

// Entities exempt from the overdue-bill popup.
const OVERDUE_POPUP_EXEMPT_ENTITIES = [1, 10, 2041, 3023];

const AdminDashboard = () => {
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false);
  const [showOverdueDialog, setShowOverdueDialog] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [range, setRange] = useState<DashboardRange>(DEFAULT_RANGE);

  const { showToast } = useToastStore();

  const { data: stats, isLoading } = useStats(range);
  const { data: billSummary } = useBillSummary();
  const { data: entity } = useEntity();

  const navigate = useNavigate();

  // Copy store URL to clipboard
  const copyStoreUrl = async () => {
    const storeUrl = `https://${entity?.storeUrl}.mart-24.com`;
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopiedUrl(true);
      showToast("Store URL copied to clipboard!", "success");
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      showToast("Failed to copy URL", "error");
    }
  };

  // Check for onboarding completion and show welcome animation
  useEffect(() => {
    const onboardingCompleted = localStorage.getItem('onboarding_completed');
    if (onboardingCompleted === 'true') {
      setShowWelcomeAnimation(true);
      localStorage.removeItem('onboarding_completed');
      setTimeout(() => {
        setShowWelcomeAnimation(false);
      }, 5000);
    }
  }, []);

  // Show the payment reminder once per page load — dismissing it (or a background
  // refetch) won't re-open it, but a refresh / reopen counts as a new load.
  useEffect(() => {
    if (overdueDialogShown || !billSummary || billSummary.totalAmount <= 0) return;
    // Wait for the entity so exempt entities never briefly see the popup.
    if (!entity) return;
    if (OVERDUE_POPUP_EXEMPT_ENTITIES.includes(entity.entityId)) return;

    // Overdue if the backend flags it OR the due date has already passed.
    const dueDate = billSummary.nearestDueDate ? new Date(billSummary.nearestDueDate) : null;
    const dueDatePassed = dueDate ? dueDate.getTime() < Date.now() : false;

    if (billSummary.isOverdue || dueDatePassed) {
      overdueDialogShown = true;
      setShowOverdueDialog(true);
    }
  }, [billSummary, entity]);

  const overdueDueDate = billSummary?.nearestDueDate
    ? new Date(billSummary.nearestDueDate).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dateStr = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  const firstName = entity?.name?.trim().split(" ")[0];

  return (
    <div className={`relative min-h-screen overflow-hidden ${pageBg}`}>
      {/* Ambient AI aurora — soft pastel orbs drifting behind the content */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <span className="ai-orb ai-orb-a" />
        <span className="ai-orb ai-orb-b" />
        <span className="ai-orb ai-orb-c" />
      </div>

      {/* WhatsApp Support Button */}
      <WhatsAppSupportButton />

      {/* Overdue bill reminder */}
      {showOverdueDialog && billSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-500 overflow-hidden">
            <div className="bg-gradient-to-r from-rose-600 to-red-600 p-6 text-center">
              <div className="w-14 h-14 bg-white/15 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Payment Overdue</h2>
              <p className="text-white/90 text-sm mt-1">Please clear your bill to avoid service interruption.</p>
            </div>

            <div className="p-6">
              <div className="bg-rose-50 rounded-xl p-4 border border-rose-100 mb-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Amount due</span>
                  <span className="text-lg font-bold text-gray-900">₹{billSummary.totalAmount.toLocaleString()}</span>
                </div>
                {overdueDueDate && (
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-rose-100">
                    <span className="text-sm text-gray-600">Due date</span>
                    <span className="text-sm font-semibold text-rose-600">{overdueDueDate}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowOverdueDialog(false);
                    navigate('/billing');
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-gradient-to-r from-rose-600 to-red-600 text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  <CreditCard className="w-5 h-5" />
                  Clear Bill Now
                </button>
                <button
                  onClick={() => setShowOverdueDialog(false)}
                  className="w-full px-4 py-3 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition-all duration-300"
                >
                  Remind me later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Dialog */}
      {showWelcomeAnimation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-500">
            {/* Header with celebration */}
            <div className="relative bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 rounded-t-2xl p-6 text-center overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-purple-600/20 backdrop-blur-3xl"></div>
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/10 rounded-full blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-white/10 rounded-full blur-xl"></div>

              <div className="relative z-10">
                {/* Celebration icons */}
                <div className="flex justify-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce animation-delay-100">
                    🎉
                  </div>
                  <div className="w-10 h-10 bg-pink-400 rounded-full flex items-center justify-center animate-bounce animation-delay-200">
                    🚀
                  </div>
                  <div className="w-10 h-10 bg-green-400 rounded-full flex items-center justify-center animate-bounce animation-delay-300">
                    ✨
                  </div>
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Welcome to Mart-24! 🎊
                </h2>
                <p className="text-white/90 text-sm md:text-base">
                  Your store is ready to shine!
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  🎯 Ready to get started?
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Let's make your store amazing! Add some products to start selling, or explore your dashboard first.
                </p>
              </div>

              {/* Quick Actions */}
              <div className="space-y-3 mb-6">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      setShowWelcomeAnimation(false);
                      navigate('/products');
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <Package className="w-5 h-5" />
                    Add Products
                  </button>

                  <button
                    onClick={() => setShowWelcomeAnimation(false)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition-all duration-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Explore Dashboard First
                  </button>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    💡
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-indigo-900 mb-1">
                      Pro Tip
                    </h4>
                    <p className="text-xs text-indigo-700 leading-relaxed">
                      Start by adding your best-selling products. You can always add more later!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-9 space-y-6 sm:space-y-8">

        {/* Page header: greeting + store link + date filter */}
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              <span className="text-xs font-medium text-gray-500">{dateStr}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900">
              {greeting}{firstName ? <>, <span className="text-indigo-600">{firstName}</span></> : ""}
            </h1>
            <p className="text-sm text-gray-500 mt-1.5">Here's how your store is performing today.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {entity?.storeUrl && (
              <div className={`${chip} flex items-center gap-1.5 pl-3 pr-1 py-1`}>
                <Globe className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-600 truncate max-w-[150px] sm:max-w-[200px]">
                  {entity.storeUrl}.mart-24.com
                </span>
                <button
                  onClick={copyStoreUrl}
                  className="p-1.5 rounded-full text-gray-500 hover:bg-gray-900/5 transition-colors"
                  title="Copy URL"
                >
                  {copiedUrl ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                <a
                  href={`https://${entity.storeUrl}.mart-24.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-full text-gray-500 hover:bg-gray-900/5 transition-colors"
                  title="Visit Store"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            )}
            <DashboardFilter value={range} onChange={setRange} />
          </div>
        </header>

        {/* AI hero — the single, intentional splash of animated colour */}
        <div
          onClick={() => navigate('/agent')}
          className="ai-gradient group relative overflow-hidden rounded-2xl cursor-pointer"
        >
          <div className="ai-shine pointer-events-none absolute inset-0" />
          <div className="relative flex items-center justify-between gap-3 p-4 sm:p-5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-semibold text-base leading-tight flex items-center gap-1.5">
                  AI Store Designer
                  <Sparkles className="w-3.5 h-3.5 text-white/80" />
                </p>
                <p className="text-white/80 text-xs mt-1 leading-tight truncate">
                  Redesign your storefront in seconds — just describe it
                </p>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/agent');
              }}
              className="flex-shrink-0 bg-white text-indigo-600 px-4 py-2 rounded-xl font-semibold text-sm hover:bg-white/90 transition-colors flex items-center gap-1.5"
            >
              <Wand2 className="w-4 h-4" />
              <span className="hidden sm:inline">Customize</span>
            </button>
          </div>
        </div>

        {/* Outstanding bill — quiet frosted alert */}
        {billSummary && billSummary.totalAmount > 0 && (
          <div className="bg-rose-50/70 backdrop-blur-xl ring-1 ring-rose-500/10 rounded-2xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="w-10 h-10 bg-white/80 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-rose-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-rose-500 uppercase tracking-wider mb-0.5">Outstanding Bill</p>
                  <p className="text-xl font-bold text-gray-900">₹{billSummary.totalAmount.toLocaleString()}</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/billing')}
                className="px-4 py-2 bg-rose-500 text-white text-sm font-medium rounded-xl hover:bg-rose-600 transition-colors flex items-center gap-2 flex-shrink-0"
              >
                <CreditCard className="w-4 h-4 hidden sm:block" />
                <span>View Bills</span>
              </button>
            </div>
          </div>
        )}

        {/* KPI panel — big numbers, whitespace-separated, one glass surface */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className={sectionTitle}>Overview</h2>
            <p className={sectionSub}>{rangeLabel(range)}</p>
          </div>
          <div className={`${card} p-5 sm:p-6`}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-7 sm:gap-x-8">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="h-2.5 bg-gray-200/70 rounded-full w-1/2 mb-3.5"></div>
                    <div className="h-7 bg-gray-200/70 rounded-lg w-2/3"></div>
                  </div>
                ))
              ) : (
                <>
                  <StatsCard title="Sales" value={stats?.totalSales!} color="bg-emerald-500" isCurrency />
                  <StatsCard title="Orders" value={stats?.totalOrders!} color="bg-indigo-500" />
                  <StatsCard title="Products Sold" value={stats?.totalProducts!} color="bg-violet-500" />
                  <StatsCard title="Total Customers" value={stats?.totalCustomers!} color="bg-amber-500" />
                </>
              )}
            </div>
          </div>
        </section>

        {/* Daily Stats Histogram */}
        <DailyStatsChart range={range} />

        {/* Recent Orders */}
        <div className={`${card} overflow-hidden`}>
          <div className="p-4 sm:p-5 flex items-center justify-between border-b border-gray-100">
            <h2 className={sectionTitle}>Recent Orders</h2>
            <span className="text-xs font-medium text-gray-400">Last 7 days</span>
          </div>
          <OrderList status="recent" />
        </div>

      </main>

      <style>{`
        .animation-delay-100 { animation-delay: 0.1s; }
        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-300 { animation-delay: 0.3s; }
        .animation-delay-500 { animation-delay: 0.5s; }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }

        /* Ambient AI aurora — soft pastel orbs drifting behind the page */
        .ai-orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(72px);
          opacity: 0.6;
          will-change: transform;
        }
        .ai-orb-a {
          width: 460px; height: 460px; top: -150px; left: -110px;
          background: radial-gradient(circle at center, #c7d2fe 0%, rgba(199,210,254,0) 70%);
          animation: orbDriftA 24s ease-in-out infinite alternate;
        }
        .ai-orb-b {
          width: 420px; height: 420px; top: 8%; right: -130px;
          background: radial-gradient(circle at center, #ddd6fe 0%, rgba(221,214,254,0) 70%);
          animation: orbDriftB 28s ease-in-out infinite alternate;
        }
        .ai-orb-c {
          width: 540px; height: 540px; bottom: -200px; left: 28%;
          background: radial-gradient(circle at center, #bae6fd 0%, rgba(186,230,253,0) 70%);
          animation: orbDriftC 32s ease-in-out infinite alternate;
        }
        @keyframes orbDriftA { from { transform: translate3d(0,0,0) scale(1); } to { transform: translate3d(60px,80px,0) scale(1.15); } }
        @keyframes orbDriftB { from { transform: translate3d(0,0,0) scale(1); } to { transform: translate3d(-70px,50px,0) scale(1.12); } }
        @keyframes orbDriftC { from { transform: translate3d(0,0,0) scale(1.05); } to { transform: translate3d(50px,-60px,0) scale(1); } }

        /* Animated gradient for the AI hero */
        .ai-gradient {
          background: linear-gradient(120deg, #6366f1, #8b5cf6, #0ea5e9, #6366f1);
          background-size: 300% 300%;
          animation: aiGradientShift 12s ease infinite;
        }
        @keyframes aiGradientShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }

        /* Subtle moving sheen across the AI hero */
        .ai-shine {
          background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%);
          background-size: 200% 100%;
          animation: aiShine 6s ease-in-out infinite;
        }
        @keyframes aiShine { 0% { background-position: 150% 0; } 100% { background-position: -50% 0; } }

        @media (prefers-reduced-motion: reduce) {
          .ai-orb, .ai-gradient, .ai-shine { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
